/**
 * useNotifications Hook
 * Fetches notifications when the active app changes and exposes
 * the current app's unread count and notification list from the store.
 */

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { kciEventBus } from '../eventBus/KCiEventBus';
import { KCI_APP_IDS } from '../eventBus/appIds';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteAllNotifications,
} from '../services/notificationService';

export function useNotifications() {
  const { state, dispatch } = useAppStore();
  const { currentApp } = state.navigation;
  const { items, loading, lastFetchedApp, error } = state.notifications;
  const hasLoadedNotifications = useRef(false);
  const notifiedErrorIds = useRef(new Set<string>());

  const refresh = useCallback(() => {
    dispatch({ type: 'SET_LAST_FETCHED_APP', payload: { appId: '' } });
  }, [dispatch]);

  // Fetch notifications when the active app changes
  useEffect(() => {
    if (currentApp === lastFetchedApp) return;

    let cancelled = false;

    const load = async () => {
      dispatch({ type: 'SET_NOTIFICATIONS_LOADING', payload: { loading: true } });

      try {
        const data = await fetchNotifications(currentApp);
        if (!cancelled) {
                    const previousIds = new Set(items.map(item => item.id));
          if (!hasLoadedNotifications.current) {
            data.filter(item => item.kind === 'action-required' || item.notificationState?.toLowerCase() === 'error')
              .forEach(item => notifiedErrorIds.current.add(item.id));
            hasLoadedNotifications.current = true;
          } else {
            const latestError = [...data]
              .filter(item =>
                !previousIds.has(item.id) &&
                !notifiedErrorIds.current.has(item.id) &&
                (item.kind === 'action-required' || item.notificationState?.toLowerCase() === 'error')
              )
              .sort((left, right) => right.createdAt - left.createdAt)[0];

            if (latestError) {
              notifiedErrorIds.current.add(latestError.id);
            }
          }
          dispatch({ type: 'SET_LAST_FETCHED_APP', payload: { appId: currentApp } });
          dispatch({ type: 'SET_NOTIFICATIONS', payload: { items: data } });
        }
      } catch (err) {
        console.error('[Notifications] Failed to fetch:', err);
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load notifications';
          dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: msg } });
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentApp, lastFetchedApp, dispatch]);

  useEffect(() => {
    const refreshEventTypes = [
      'WorkspaceNotificationRefreshRequested',
      'WorkspaceActionCompleted',
      'WorkspaceActionFailed',
      'WorkspaceMilestoneUpdated',
    ];
    let lastRefreshAt = 0;

    const handler = (event: { source: string; type: string }) => {
      if (event.source !== KCI_APP_IDS.WORKSPACE_APP && event.source !== KCI_APP_IDS.SHELL) {
        return;
      }
      const now = Date.now();
      if (now - lastRefreshAt < 750) return;
      lastRefreshAt = now;
      refresh();
    };
    const unsubscribers = [...refreshEventTypes].map(type => kciEventBus.subscribe(type, handler));

    return () => unsubscribers.forEach(unsubscribe => unsubscribe());
  }, [refresh]);

  // Derived unread count for the current app
  const unreadCount = useMemo(
    () => items.filter(n => !n.read).length,
    [items],
  );

  const markRead = useCallback(
    async (id: string, read: boolean) => {
      const current = items.find(n => n.id === id);
      if (!current) return;

      dispatch({ type: 'MARK_NOTIFICATION_READ', payload: { id, read } });
      try {
        await markNotificationRead(id, read);
      } catch (err) {
        dispatch({ type: 'MARK_NOTIFICATION_READ', payload: { id, read: current.read } });
        const msg = err instanceof Error ? err.message : 'Failed to update notification';
        dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: msg } });
      }
    },
    [dispatch, items],
  );

  const markAsUnread = useCallback(
    async (id: string) => {
      dispatch({ type: 'MARK_NOTIFICATION_UNREAD', payload: { id } });
      try {
        await markNotificationRead(id, false);
      } catch (err) {
        dispatch({ type: 'MARK_NOTIFICATION_READ', payload: { id, read: true } });
        const msg = err instanceof Error ? err.message : 'Failed to update notification';
        dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: msg } });
      }
    },
    [dispatch],
  );

  const markAllRead = useCallback(
    async () => {
      const unreadItems = items.filter(n => !n.read);
      if (unreadItems.length === 0) return;

      dispatch({ type: 'MARK_ALL_NOTIFICATIONS_READ' });

      try {
        await markAllNotificationsRead(currentApp, unreadItems.map(item => item.id));
      } catch (err) {
        for (const item of unreadItems) {
          dispatch({ type: 'MARK_NOTIFICATION_READ', payload: { id: item.id, read: false } });
        }
        const msg = err instanceof Error ? err.message : 'Failed to mark all notifications read';
        dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: msg } });
      }
    },
    [dispatch, items, currentApp],
  );

  const deleteNotif = useCallback(
    async (id: string) => {
      const existing = items.find(item => item.id === id);
      if (!existing) return;

      dispatch({ type: 'DELETE_NOTIFICATION', payload: { id } });
      try {
        await deleteNotification(id);
      } catch (err) {
        dispatch({ type: 'SET_NOTIFICATIONS', payload: { items } });
        const msg = err instanceof Error ? err.message : 'Failed to delete notification';
        dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: msg } });
      }
    },
    [dispatch, items],
  );

  const deleteAll = useCallback(
    async () => {
      const snapshot = items;
      dispatch({ type: 'DELETE_ALL_NOTIFICATIONS' });
      try {
        await deleteAllNotifications(currentApp);
      } catch (err) {
        dispatch({ type: 'SET_NOTIFICATIONS', payload: { items: snapshot } });
        const msg = err instanceof Error ? err.message : 'Failed to delete all notifications';
        dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: msg } });
      }
    },
    [dispatch, items, currentApp],
  );

  const clearError = useCallback(
    () => dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: null } }),
    [dispatch],
  );

  const retry = useCallback(() => {
    dispatch({ type: 'SET_NOTIFICATIONS_ERROR', payload: { error: null } });
    dispatch({ type: 'SET_LAST_FETCHED_APP', payload: { appId: '' } });
  }, [dispatch]);

  const sortedNotifications = useMemo(
    () => [...items].sort((a, b) => b.createdAt - a.createdAt),
    [items],
  );

  return {
    notifications: sortedNotifications,
    unreadCount,
    loading,
    error,
    markRead,
    markAsUnread,
    markAllRead,
    deleteNotif,
    deleteAll,
    clearError,
    retry,
    refresh,
  };
}
